import axios, { AxiosError } from "axios";

import { appConfig } from "@/config/app.config";
import { extractAudioFromVideo } from "@/lib/media";
import type {
  SummaryRequest,
  SummaryResult,
  TranscriptRequest,
  TranscriptResult,
} from "@/types/transcript";
import type { ApiErrorBody } from "@/types/api";
import type { WaitlistJoinRequest, WaitlistJoinResult } from "@/types/waitlist";

const api = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: 120000,
});

function toError(
  error: unknown,
  fallback = "The transcript request failed. Please try again.",
): Error {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorBody>;
    const message =
      axiosError.response?.data?.error?.message ||
      axiosError.message ||
      fallback;
    return new Error(message);
  }

  return error instanceof Error ? error : new Error(fallback);
}

export async function generateTranscript(
  payload: TranscriptRequest,
): Promise<TranscriptResult> {
  try {
    if (payload.sourceType === "link") {
      const response = await api.post<TranscriptResult>(
        "/transcript",
        {
          mediaUrl: payload.mediaUrl,
          language: payload.language,
          title: payload.title,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    }

    const form = new FormData();
    form.append("sourceType", payload.sourceType);

    let uploadFile = payload.file;
    // Workers AI (Whisper) cannot decode video containers — extract audio first.
    if (uploadFile.type.startsWith("video/")) {
      try {
        uploadFile = await extractAudioFromVideo(
          uploadFile,
          payload.filename || "video-transcript-media",
        );
      } catch {
        // If extraction fails, fall back to the original file
      }
    }

    form.append(
      "file",
      uploadFile,
      uploadFile instanceof File
        ? uploadFile.name
        : payload.filename || "video-transcript-media.wav",
    );
    if (payload.language) form.append("language", payload.language);
    if (payload.title) form.append("title", payload.title);

    const response = await api.post<TranscriptResult>("/transcript", form);
    return response.data;
  } catch (error) {
    throw toError(error);
  }
}

export async function summarizeTranscript(
  payload: SummaryRequest,
): Promise<SummaryResult> {
  try {
    const response = await api.post<SummaryResult>("/summarize", payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    throw toError(error);
  }
}

export async function joinPremiumWaitlist(
  payload: WaitlistJoinRequest,
): Promise<WaitlistJoinResult> {
  try {
    const response = await api.post<WaitlistJoinResult>("/waitlist", payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    throw toError(error, "The waitlist signup failed. Please try again.");
  }
}
